import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useSobriety } from '@/contexts/SobrietyContext';
import { useLanguage } from '@/contexts/LanguageContext';
import PageHeader from '@/components/PageHeader';
import TertiaryButton from '@/components/TertiaryButton';

export default function SobrietyRelapseScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const { setupSobrietyRelapse } = useSobriety();
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateReminder = () => {
    console.log('Creating reminder note for relapse');
    router.push({
      pathname: '/add-note',
      params: {
        isRelapseNote: 'true',
        noteTitle: t('sobriety.relapse.noteTitle')
      }
    });
  };

  const handleBackToHome = async () => {
    console.log('Going back to home after relapse');

    setIsLoading(true);
    try {
      await setupSobrietyRelapse(async () => {
      });

      // PRD §5.5 — `sobriety_relapse_logged`. `previous_streak_days` is
      // not surfaced to this screen by SobrietyContext today; default to
      // 0 (the helper requires the field). When SobrietyContext exposes
      // the prior streak, replace the zero with the real count and update
      // the PRD note.
      import('@/utils/clarityEvents')
        .then(({ logSobrietyRelapseLogged }) =>
          logSobrietyRelapseLogged({ previous_streak_days: 0 })
        )
        .catch(() => {});

      router.replace('/diary');
    } catch (error) {
      console.error('Failed to setup sobriety relapse:', error);
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={t('sobriety.relapse.title')} 
        onBack={() => router.back()}
        showBackButton={false}
        backgroundColor="#F0EEE4"
      />

      <View style={styles.content}>
        <View style={styles.illustrationContainer}>
          <Image
            source={require('@/assets/images/effortless.png')}
            style={styles.illustrationImage}
            resizeMode="contain"
          />
        </View>

        <Text style={styles.mainTitle}>
          {t('sobriety.relapse.mainTitle')}
        </Text>

        <Text style={styles.subtitle}>
          {t('sobriety.relapse.subtitle')}
        </Text>
      </View>

      <View style={[styles.buttonContainer, { paddingBottom: insets.bottom + 24 }]}>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={handleCreateReminder}
        >
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path d="M3.5 20.6561C3.5 20.6561 3.99166 8.65565 17.0741 3.8347C28.2815 0.847713 14.6693 12.4665 14.6693 12.4665L9.06563 13.0168L11.6659 15.075C9.11398 16.8688 6.5631 17.7555 4.0112 17.7318" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path opacity="0.4" d="M11.2168 20.6562H17.6269" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
          <Text style={styles.primaryButtonText}>
            {t('sobriety.relapse.reminderButton')}
          </Text>
        </TouchableOpacity>

        <TertiaryButton
          title={t('sobriety.relapse.continueButton')}
          onPress={handleBackToHome}
          loading={isLoading}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0EEE4',
  },
  content: {
    flex: 1,
    paddingHorizontal: 40,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  illustrationContainer: {
    width: 260,
    height: 260,
    marginBottom: 16,
  },
  illustrationImage: {
    width: '100%',
    height: '100%',
  },
  mainTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 29,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 20,
  },
  buttonContainer: {
    paddingHorizontal: 24,
    gap: 8,
  },
  primaryButton: {
    backgroundColor: '#151515',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 56,
    flexDirection: 'row',
    gap: 12,
  },
  primaryButtonText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#FFFFFF',
    lineHeight: 20,
    textAlign: 'center',
  },
});
